import type { MouseEvent } from 'react';
import styled from 'styled-components';
import { Tooltip } from '@mui/material';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { OverflowTooltip } from '@elementary/common-ui/components';
import { fontSize, space } from '@elementary/common-ui/style/utils';

export type EventLink = {
  url?: string;
  onClick?: (event: MouseEvent<HTMLAnchorElement>) => void;
  tooltip?: string;
  /**
   * The substring of the label that should be wrapped as the clickable anchor.
   * When set, only this portion becomes the link; the rest renders as plain
   * text. When omitted, the entire label is wrapped as a fallback.
   */
  linkedText?: string;
};

export type EventLabelResult = {
  label: string;
  link?: EventLink;
};

const EventText = styled(OverflowTooltip)`
  font-size: ${fontSize('md')};
  color: ${({ theme }) => theme.palette.content.secondary};
  min-width: 0;
  flex: 0 1 auto;
  width: auto;
  max-width: 100%;
`;

const EventLinkAnchor = styled.a`
  display: inline-flex;
  align-items: center;
  gap: ${space('xs')};
  color: inherit;
  text-decoration: none;
  min-width: 0;
  flex: 0 1 auto;

  &:hover {
    text-decoration: underline;
    color: ${({ theme }) => theme.palette.chart.neutral};
  }
`;

const ExternalLinkIcon = styled(OpenInNewIcon)`
  font-size: ${fontSize('sm')};
  flex-shrink: 0;
  color: ${({ theme }) => theme.palette.content.tertiary};
`;

type Props = {
  label: string;
  link?: EventLink;
};

export const TimelineEventLabel = ({ label, link }: Props) => {
  if (!link) {
    return <EventText>{label}</EventText>;
  }

  const isExternalLink = !!link.url;
  const onAnchorClick = (event: MouseEvent<HTMLAnchorElement>) => {
    event.stopPropagation();
    if (!isExternalLink) {
      event.preventDefault();
    }
    link.onClick?.(event);
  };

  const { linkedText } = link;
  if (
    linkedText &&
    linkedText.length > 0 &&
    label.endsWith(linkedText) &&
    label.length > linkedText.length
  ) {
    const prefix = label.slice(0, label.length - linkedText.length);
    const anchor = (
      <EventLinkAnchor
        href={link.url ?? '#'}
        target={isExternalLink ? '_blank' : undefined}
        rel={isExternalLink ? 'noopener noreferrer' : undefined}
        onClick={onAnchorClick}
      >
        {linkedText}
        {isExternalLink && <ExternalLinkIcon />}
      </EventLinkAnchor>
    );
    return (
      <EventText>
        {prefix}
        {link.tooltip ? (
          <Tooltip title={link.tooltip}>{anchor}</Tooltip>
        ) : (
          anchor
        )}
      </EventText>
    );
  }

  const anchor = (
    <EventLinkAnchor
      href={link.url ?? '#'}
      target={isExternalLink ? '_blank' : undefined}
      rel={isExternalLink ? 'noopener noreferrer' : undefined}
      onClick={onAnchorClick}
    >
      <EventText>{label}</EventText>
      {isExternalLink && <ExternalLinkIcon />}
    </EventLinkAnchor>
  );
  return link.tooltip ? (
    <Tooltip title={link.tooltip}>{anchor}</Tooltip>
  ) : (
    anchor
  );
};
